import * as React from 'react';

import {
  Button,
  DataGrid,
  FontSemiBold,
  FloatingToolbar,
  ListScreenLayout,
  Pagination,
  SearchField,
  useDataGridSelection,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlus, Trash } from 'lucide-react';

import { AccountRecord } from '../../_graphql/queryAccounts';
import * as accountEditingState from '../../_state/accountEditing.state';
import * as accountListState from '../../_state/accountList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { crmRoutes } from '../../routes';

import { AccountCreateDialog } from './AccountCreateDialog';
import { AccountDeleteConfirmDialog } from './AccountDeleteConfirmDialog';
import { useAccountListColumns } from './hooks/useAccountListColumns';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

//
// ⚛️ ⚛️ ⚛️ ACCOUNT LIST VIEW ⚛️ ⚛️ ⚛️
//
export const AccountListView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  const [selectedRows, setSelectedRows] = React.useState<AccountRecord[]>([]);
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [singleDeleteAccount, setSingleDeleteAccount] =
    React.useState<AccountRecord | null>(null);

  const { data, isFetching, loadingState, error } = useAtomValue(
    accountListState.$accountList,
  );
  const [searchTerm, setSearchTerm] = useDebouncedAtom(
    accountListState.$searchTerm,
    300,
  );
  const [offset, setOffset] = useAtom(accountListState.$offset);
  const [limit, setLimit] = useAtom(accountListState.$limit);

  const deleteAccount = useSetAtom(accountEditingState.$deleteRemoteAccount);

  const toast = useToast();
  const accounts = data?.accounts ?? [];
  const total = data?.total ?? 0;
  const handleSelectionChange = useDataGridSelection(accounts, setSelectedRows);

  const emptyPanelProps = React.useMemo(
    () => ({
      title: t.accountsView.emptyState.title,
      description: <span>{t.accountsView.emptyState.description}</span>,
      centered: true,
    }),
    [t],
  );

  const handleRowClick = React.useCallback(
    (record: AccountRecord) =>
      navigate(crmRoutes.accountDetails, {
        replace: true,
        params: { accountId: record.id },
      }),
    [navigate],
  );
  const handleDeletePressed = React.useCallback(() => {
    setShowDeleteDialog(true);
  }, []);
  const handleConfirmDelete = React.useCallback(async () => {
    try {
      if (singleDeleteAccount) {
        await deleteAccount({ id: singleDeleteAccount.id });
      } else {
        await Promise.all(
          selectedRows.map((row) => deleteAccount({ id: row.id })),
        );
        setSelectedRows([]);
      }
      toast.success(t.accountsView.messages.deleteSuccess);
      setShowDeleteDialog(false);
      setSingleDeleteAccount(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t.accountsView.messages.error,
      );
    }
  }, [singleDeleteAccount, selectedRows, deleteAccount, toast, t]);
  const handlePaginationChanged = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleSearchChanged = React.useCallback(
    (value: string) =>
      setSearchTerm(value, {
        replace: true,
      }),
    [setSearchTerm],
  );
  const handleNewAccountClicked = React.useCallback(() => {
    setShowCreateDialog(true);
  }, []);
  const handleCreateDialogOpenChange = React.useCallback(
    (open: boolean) => {
      setShowCreateDialog(open);
    },
    [setShowCreateDialog],
  );

  const handleRowDeleteAccount = React.useCallback(
    (params: { id: string }) => {
      const account = accounts.find((a) => a.id === params.id);
      if (account) {
        setSingleDeleteAccount(account);
        setShowDeleteDialog(true);
      }
    },
    [accounts],
  );

  const columns = useAccountListColumns({
    onRowClick: handleRowClick,
    onDeleteAccount: handleRowDeleteAccount,
  });

  return (
    <>
      <ListScreenLayout
        filters={
          <SearchField
            autoFocus
            aria-label={t.accountsView.toolbar.search}
            className="w-80"
            placeholder={t.accountsView.toolbar.search}
            value={searchTerm}
            onChange={handleSearchChanged}
          />
        }
        actions={
          <Button
            color="primary"
            icon={<LucidePlus />}
            onPress={handleNewAccountClicked}
          >
            {t.accountsView.toolbar.create}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChanged}
            />
          ) : undefined
        }
        className="@AccountListView"
      >
        <DataGrid<AccountRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          aria-label={t.accountsView.table.aria}
          selectionMode="multiple"
          onSelectionChange={handleSelectionChange}
          fullWidth
          stickyHeader
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: String(t.accountsView.content.error).replace(
                    '{{message}}',
                    error.message,
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <div className="flex items-center gap-2">
          <div className="flex min-w-[165px] items-center justify-end gap-1.5 px-3 text-sm text-neutral/80">
            <FontSemiBold>{selectedRows.length}</FontSemiBold>
            {selectedRows.length === 1 ? 'account' : 'accounts'} selected
          </div>
          <Button
            variant="outline"
            color="failure"
            icon={<Trash />}
            isDisabled={selectedRows.length < 1}
            onPress={handleDeletePressed}
          >
            {t.accountsView.toolbar.delete}
          </Button>
        </div>
      </FloatingToolbar>

      <AccountDeleteConfirmDialog
        isOpen={showDeleteDialog}
        accountName={singleDeleteAccount?.name}
        accountCount={singleDeleteAccount ? 1 : selectedRows.length}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setSingleDeleteAccount(null);
        }}
      />

      <AccountCreateDialog
        isOpen={showCreateDialog}
        onOpenChange={handleCreateDialogOpenChange}
      />
    </>
  );
};
